'use client';

import Link from 'next/link';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Section component for consistent styling
function Section({ 
  title, 
  icon, 
  children,
  className = ''
}: { 
  title: string; 
  icon: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`mb-12 ${className}`}>
      <div className="flex items-center gap-3 mb-6 pb-3 border-b-2 border-stone-300">
        <span className="text-2xl">{icon}</span>
        <h2 className="text-2xl font-serif font-bold text-stone-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

// Info box for callouts
function InfoBox({ 
  variant = 'default',
  children 
}: { 
  variant?: 'default' | 'warning' | 'success';
  children: React.ReactNode;
}) {
  const styles = {
    default: 'bg-stone-100 border-stone-300',
    warning: 'bg-amber-50 border-amber-300',
    success: 'bg-green-50 border-green-300'
  };
  
  return (
    <div className={`border-l-4 ${styles[variant]} p-4 rounded-r-lg my-4`}>
      {children}
    </div>
  );
}

// Pipeline diagram component
function PipelineDiagram() {
  return (
    <div className="bg-white/80 rounded-xl p-6 border border-stone-200 overflow-x-auto">
      <div className="min-w-[600px]">
        {/* Main flow */}
        <div className="flex items-center justify-between gap-2 mb-6">
          {/* Step 1: RSS */}
          <div className="flex-1 text-center">
            <div className="w-16 h-16 mx-auto bg-blue-100 rounded-xl flex items-center justify-center mb-2">
              <span className="text-2xl">📡</span>
            </div>
            <div className="font-mono text-xs font-semibold text-blue-600 uppercase">RSS Feeds</div>
          </div>
          
          <div className="text-stone-400 text-2xl">→</div>
          
          {/* Step 2: Clustering */}
          <div className="flex-1 text-center">
            <div className="w-16 h-16 mx-auto bg-purple-100 rounded-xl flex items-center justify-center mb-2">
              <span className="text-2xl">🔗</span>
            </div>
            <div className="font-mono text-xs font-semibold text-purple-600 uppercase">Clustering</div>
          </div>
          
          <div className="text-stone-400 text-2xl">→</div>
          
          {/* Step 3: Curator */}
          <div className="flex-1 text-center">
            <div className="w-16 h-16 mx-auto bg-amber-100 rounded-xl flex items-center justify-center mb-2">
              <span className="text-2xl">✂️</span>
            </div>
            <div className="font-mono text-xs font-semibold text-amber-600 uppercase">Curador</div>
          </div>
          
          <div className="text-stone-400 text-2xl">→</div>
          
          {/* Step 4: Analysis */}
          <div className="flex-1 text-center">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-xl flex items-center justify-center mb-2">
              <span className="text-2xl">🤖</span>
            </div>
            <div className="font-mono text-xs font-semibold text-green-600 uppercase">Análisis IA</div>
          </div>
          
          <div className="text-stone-400 text-2xl">→</div>
          
          {/* Step 5: Display */}
          <div className="flex-1 text-center">
            <div className="w-16 h-16 mx-auto bg-stone-800 rounded-xl flex items-center justify-center mb-2">
              <span className="text-2xl">📰</span>
            </div>
            <div className="font-mono text-xs font-semibold text-stone-600 uppercase">Portada</div>
          </div>
        </div>
        
        {/* Details row */}
        <div className="flex items-start justify-between gap-2 text-xs text-stone-500 border-t border-stone-200 pt-4">
          <div className="flex-1 text-center px-1">
            <p>14+ medios</p>
            <p className="text-stone-400">cada 30 min</p>
          </div>
          <div className="w-6"></div>
          <div className="flex-1 text-center px-1">
            <p>Agrupación</p>
            <p className="text-stone-400">por similitud</p>
          </div>
          <div className="w-6"></div>
          <div className="flex-1 text-center px-1">
            <p>Filtro editorial</p>
            <p className="text-stone-400">relevancia política</p>
          </div>
          <div className="w-6"></div>
          <div className="flex-1 text-center px-1">
            <p>Síntesis</p>
            <p className="text-stone-400">hechos + narrativas</p>
          </div>
          <div className="w-6"></div>
          <div className="flex-1 text-center px-1">
            <p>Tú decides</p>
            <p className="text-stone-400">todas las versiones</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AlgoritmoPage() {
  const today = format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es });
  const todayCapitalized = today.charAt(0).toUpperCase() + today.slice(1);

  return (
    <div className="min-h-screen bg-[#FDF6E3]">
      {/* Header */}
      <header className="border-b-2 border-stone-800 bg-[#FDF6E3] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {/* Top bar: Date */}
          <div className="flex items-center justify-between py-2 border-b border-stone-200">
            <span className="text-xs text-stone-500 font-mono uppercase tracking-wider">
              {todayCapitalized}
            </span>
            <span className="text-xs text-stone-400">España</span>
          </div>
          
          {/* Masthead */}
          <div className="text-center py-4">
            <Link href="/">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-black tracking-tight text-stone-900 hover:text-stone-700 transition-colors">
                NEOTRAL
              </h1>
            </Link>
            <p className="text-xs sm:text-sm text-stone-500 italic mt-1 font-serif">
              Todas las versiones. Tú decides.
            </p>
          </div>
          
          {/* Navigation */}
          <nav className="flex justify-center gap-6 sm:gap-8 py-2 border-t border-stone-200 text-sm overflow-x-auto no-scrollbar">
            <Link href="/" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Hoy
            </Link>
            <Link href="/radar" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Radar
            </Link>
            <Link href="/medios" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Medios
            </Link>
            <Link href="/silenciadas" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Silenciadas
            </Link>
            <Link href="/algoritmo" className="font-serif font-semibold text-stone-900 whitespace-nowrap">
              Algoritmo
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Page Header */}
        <header className="mb-12 text-center">
          <h1 className="text-4xl sm:text-5xl font-serif font-black text-stone-900 mb-4">
            🔍 Cómo Funciona Neotral
          </h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
            Te explicamos cómo procesamos las noticias, qué decisiones tomamos, 
            y qué limitaciones tenemos.
          </p>
        </header>

        {/* ============================================ */}
        {/* PIPELINE OVERVIEW */}
        {/* ============================================ */}
        <Section title="El Pipeline" icon="⚙️">
          <p className="text-stone-700 mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Así procesamos las noticias desde que se publican hasta que las ves en portada:
          </p>
          
          <PipelineDiagram />
          
          <InfoBox>
            <p className="text-sm text-stone-700">
              <strong>Actualización:</strong> El pipeline se ejecuta automáticamente cada 30 minutos. 
              Los artículos aparecen en portada unos minutos después de publicarse.
            </p>
          </InfoBox>
        </Section>

        {/* ============================================ */}
        {/* STEP 1: RSS SCRAPING */}
        {/* ============================================ */}
        <Section title="1. Recolección de Noticias" icon="📡">
          <p className="text-stone-700 mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Cada 30 minutos consultamos los feeds RSS públicos de cada medio.
          </p>
          
          <div className="bg-white/60 rounded-lg p-6 border border-stone-200 mb-6">
            <h3 className="font-mono text-sm font-semibold text-stone-500 uppercase tracking-wider mb-4">
              Qué capturamos
            </h3>
            <ul className="space-y-2 text-sm text-stone-700">
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Titular del artículo</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Descripción/extracto</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>URL del artículo</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Fecha de publicación</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Imagen principal (si está disponible)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-stone-400">✗</span>
                <span className="text-stone-500">Contenido completo del artículo (no lo guardamos)</span>
              </li>
            </ul>
          </div>

          <h3 className="font-mono text-sm font-semibold text-stone-500 uppercase tracking-wider mb-4">
            Medios monitorizados
          </h3>
          
          <div className="grid gap-4 sm:grid-cols-2">
            {/* España */}
            <div className="bg-white/60 rounded-lg p-4 border border-stone-200">
              <h4 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
                <span>🇪🇸</span> España (14 medios)
              </h4>
              <div className="flex flex-wrap gap-2">
                {['El País', 'elDiario.es', 'Público', 'infoLibre', 'El Plural', '20 Minutos', 
                  'La Vanguardia', 'El Confidencial', 'El Periódico', 'El Mundo', 'ABC', 
                  'El Español', 'Libertad Digital', 'OK Diario'].map(medio => (
                  <span key={medio} className="px-2 py-1 bg-stone-100 rounded text-xs text-stone-700">
                    {medio}
                  </span>
                ))}
              </div>
            </div>
            
            {/* International */}
            <div className="bg-white/60 rounded-lg p-4 border border-stone-200">
              <h4 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
                <span>🌍</span> Internacional (en desarrollo)
              </h4>
              <div className="flex flex-wrap gap-2 text-stone-500 text-xs">
                <span className="px-2 py-1 bg-stone-50 rounded">🇺🇸 8 medios USA</span>
                <span className="px-2 py-1 bg-stone-50 rounded">🇬🇧 9 medios UK</span>
                <span className="px-2 py-1 bg-stone-50 rounded">🇩🇪 8 medios DE</span>
              </div>
              <p className="text-xs text-stone-400 mt-2 italic">Próximamente activos</p>
            </div>
          </div>
        </Section>

        {/* ============================================ */}
        {/* STEP 2: CLUSTERING */}
        {/* ============================================ */}
        <Section title="2. Agrupación de Noticias" icon="🔗">
          <p className="text-stone-700 mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Detectamos cuándo diferentes medios hablan de la misma historia usando un sistema híbrido
            que combina análisis de palabras clave y comprensión semántica.
          </p>

          <div className="space-y-4 mb-6">
            <div className="bg-white/60 rounded-lg p-5 border border-stone-200">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-sm font-bold text-purple-600">1</span>
                <h4 className="font-serif font-bold text-stone-900">Extracción de palabras clave</h4>
              </div>
              <p className="text-stone-600 text-sm" style={{ fontFamily: 'Georgia, serif' }}>
                Identificamos nombres propios, organizaciones, lugares y términos relevantes
                de cada artículo. "Sánchez", "Congreso", "presupuestos" son pistas para agrupar.
              </p>
            </div>

            <div className="bg-white/60 rounded-lg p-5 border border-stone-200">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-sm font-bold text-purple-600">2</span>
                <h4 className="font-serif font-bold text-stone-900">Análisis semántico (nuevo)</h4>
              </div>
              <p className="text-stone-600 text-sm mb-3" style={{ fontFamily: 'Georgia, serif' }}>
                Cada artículo se traduce matemáticamente a un "vector" de 768 números que captura
                su significado. Artículos con significados similares tendrán vectores similares,
                aunque usen palabras diferentes.
              </p>
              <p className="text-stone-500 text-xs italic">
                Ejemplo: "Crisis económica" y "Recesión financiera" se detectan como el mismo tema,
                aunque no compartan palabras exactas.
              </p>
            </div>

            <div className="bg-white/60 rounded-lg p-5 border border-stone-200">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-sm font-bold text-purple-600">3</span>
                <h4 className="font-serif font-bold text-stone-900">Cálculo de similitud híbrido</h4>
              </div>
              <p className="text-stone-600 text-sm mb-3" style={{ fontFamily: 'Georgia, serif' }}>
                Combinamos dos métricas matemáticas:
              </p>
              <ul className="space-y-2 text-sm text-stone-600">
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  <span><strong>Similitud de palabras (50%):</strong> Índice de Jaccard sobre palabras clave comunes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  <span><strong>Similitud semántica (50%):</strong> Distancia entre vectores de significado</span>
                </li>
              </ul>
              <p className="text-stone-500 text-xs mt-3">
                Si la similitud combinada supera el 15%, los artículos se agrupan.
              </p>
            </div>

            <div className="bg-white/60 rounded-lg p-5 border border-stone-200">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-sm font-bold text-purple-600">4</span>
                <h4 className="font-serif font-bold text-stone-900">Formación de clusters</h4>
              </div>
              <p className="text-stone-600 text-sm" style={{ fontFamily: 'Georgia, serif' }}>
                Agrupamos artículos similares en "clusters" (racimos). Cada cluster representa
                una historia vista desde múltiples ángulos. Evitamos que un mismo medio aparezca
                dos veces en el mismo cluster.
              </p>
            </div>
          </div>

          <InfoBox>
            <p className="text-sm text-stone-700">
              <strong>💡 Ventaja técnica:</strong> El análisis semántico funciona en español e inglés,
              y detecta el mismo tema incluso cuando los medios usan vocabulario diferente. Esto mejora
              la calidad del agrupamiento sin intervención humana.
            </p>
          </InfoBox>
        </Section>

        {/* ============================================ */}
        {/* STEP 3: EDITORIAL CURATOR */}
        {/* ============================================ */}
        <Section title="3. Curador Editorial" icon="✂️">
          <p className="text-stone-700 mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Filtramos clusters para mostrar solo noticias políticamente relevantes.
            <strong> Sí, tomamos decisiones editoriales.</strong>
          </p>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            {/* Excluded */}
            <div className="bg-red-50/50 rounded-lg p-5 border border-red-100">
              <h4 className="font-serif font-bold text-red-800 mb-3 flex items-center gap-2">
                <span>🚫</span> Excluimos
              </h4>
              <ul className="space-y-1 text-sm text-red-700">
                <li>• Prensa rosa y famosos</li>
                <li>• Resultados deportivos</li>
                <li>• Tiempo y meteorología</li>
                <li>• Horóscopo y lotería</li>
                <li>• Series, películas, estrenos</li>
                <li>• Recetas y moda</li>
              </ul>
            </div>
            
            {/* Prioritized */}
            <div className="bg-green-50/50 rounded-lg p-5 border border-green-100">
              <h4 className="font-serif font-bold text-green-800 mb-3 flex items-center gap-2">
                <span>✅</span> Priorizamos
              </h4>
              <ul className="space-y-1 text-sm text-green-700">
                <li>• Política nacional e internacional</li>
                <li>• Economía, empleo, impuestos</li>
                <li>• Justicia y corrupción</li>
                <li>• Sanidad, educación, pensiones</li>
                <li>• Inmigración y derechos</li>
                <li>• Seguridad y terrorismo</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white/60 rounded-lg p-6 border border-stone-200 mb-6">
            <h4 className="font-mono text-sm font-semibold text-stone-500 uppercase tracking-wider mb-4">
              Sistema de puntuación
            </h4>
            <p className="text-stone-600 text-sm mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Cada cluster recibe una puntuación de 0-100 basada en:
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="w-24 text-right text-sm font-medium text-stone-700">Relevancia</div>
                <div className="flex-1 h-3 bg-stone-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: '40%' }}></div>
                </div>
                <div className="w-12 text-sm text-stone-500">40%</div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-24 text-right text-sm font-medium text-stone-700">Contraste</div>
                <div className="flex-1 h-3 bg-stone-200 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500 rounded-full" style={{ width: '30%' }}></div>
                </div>
                <div className="w-12 text-sm text-stone-500">30%</div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-24 text-right text-sm font-medium text-stone-700">Importancia</div>
                <div className="flex-1 h-3 bg-stone-200 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: '30%' }}></div>
                </div>
                <div className="w-12 text-sm text-stone-500">30%</div>
              </div>
            </div>
            <p className="text-xs text-stone-500 mt-4">
              Solo publicamos clusters con puntuación ≥ 50. El "contraste de sesgo" mide 
              si izquierda y derecha cubren la noticia de forma diferente.
            </p>
          </div>
          
          <InfoBox>
            <p className="text-sm text-stone-700">
              <strong>Honestidad:</strong> Sí, decidimos qué es "relevante". No pretendemos 
              ser objetivos al 100% — pero sí intentamos ser <em>transparentes</em> sobre 
              nuestros criterios.
            </p>
          </InfoBox>
        </Section>

        {/* ============================================ */}
        {/* STEP 4: AI ANALYSIS */}
        {/* ============================================ */}
        <Section title="4. Análisis con IA" icon="🤖">
          <p className="text-stone-700 mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Usamos modelos de lenguaje (LLMs) para sintetizar y analizar cada cluster.
          </p>
          
          <div className="bg-white/60 rounded-lg p-6 border border-stone-200 mb-6">
            <h4 className="font-mono text-sm font-semibold text-stone-500 uppercase tracking-wider mb-4">
              Qué hace la IA
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-stone-400">→</span>
                <p className="text-stone-700 text-sm">
                  <strong>Titular neutral:</strong> Genera un titular que no favorece 
                  ninguna perspectiva específica.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-stone-400">→</span>
                <p className="text-stone-700 text-sm">
                  <strong>Hechos verificados:</strong> Extrae los hechos en los que 
                  coinciden todos los medios.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-stone-400">→</span>
                <p className="text-stone-700 text-sm">
                  <strong>Narrativas:</strong> Identifica cómo cada medio enfoca 
                  la historia de forma diferente.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-stone-400">→</span>
                <p className="text-stone-700 text-sm">
                  <strong>Puntos ciegos:</strong> Señala aspectos que ningún medio destaca.
                </p>
              </li>
            </ul>
          </div>
          
          <div className="bg-stone-800 text-white rounded-lg p-5">
            <p className="font-mono text-xs text-stone-400 mb-2">// Modelo usado</p>
            <p className="font-mono text-sm text-green-400">x-ai/grok-4.1-fast</p>
            <p className="text-stone-400 text-xs mt-2">
              Elegido por equilibrio entre velocidad, coste y calidad de análisis político.
            </p>
          </div>
          
          <InfoBox variant="warning">
            <p className="text-sm text-amber-800">
              <strong>⚠️ Limitación:</strong> Los LLMs pueden alucinarse o cometer errores. 
              El análisis es una <em>ayuda</em>, no la verdad absoluta. Siempre lee los 
              artículos originales.
            </p>
          </InfoBox>
        </Section>

        {/* ============================================ */}
        {/* BIAS VISUALIZATION */}
        {/* ============================================ */}
        <Section title="5. Visualización del Sesgo" icon="⚖️">
          <p className="text-stone-700 mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Clasificamos cada medio en un espectro político simplificado.
          </p>
          
          {/* Spectrum visual */}
          <div className="bg-white/60 rounded-lg p-6 border border-stone-200 mb-6">
            <div className="h-4 rounded-full bg-gradient-to-r from-red-500 via-stone-300 to-blue-500 mb-4" />
            <div className="flex justify-between text-sm font-medium">
              <span className="text-red-600">← Izquierda</span>
              <span className="text-stone-500">Centro</span>
              <span className="text-blue-600">Derecha →</span>
            </div>
            
            <div className="mt-6 grid grid-cols-3 gap-4 text-xs">
              <div className="text-center">
                <div className="bg-red-50 rounded p-2 border border-red-100">
                  <p className="font-semibold text-red-700">Izquierda</p>
                  <p className="text-red-600 mt-1">Público, elDiario, infoLibre</p>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-stone-50 rounded p-2 border border-stone-200">
                  <p className="font-semibold text-stone-700">Centro</p>
                  <p className="text-stone-600 mt-1">La Vanguardia, El Confidencial</p>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-blue-50 rounded p-2 border border-blue-100">
                  <p className="font-semibold text-blue-700">Derecha</p>
                  <p className="text-blue-600 mt-1">El Mundo, ABC, OK Diario</p>
                </div>
              </div>
            </div>
          </div>
          
          <InfoBox variant="warning">
            <p className="text-sm text-amber-800">
              <strong>⚠️ Simplificación:</strong> La clasificación izquierda/derecha es una 
              reducción extrema. Un medio puede ser conservador en economía y progresista 
              en lo social. Usamos esta escala como <em>orientación</em>, no como etiqueta 
              definitiva.
            </p>
          </InfoBox>
        </Section>

        {/* ============================================ */}
        {/* INDEPENDENCE & QUALITY */}
        {/* ============================================ */}
        <Section title="Independencia y Control de Calidad" icon="🔬">
          <p className="text-stone-700 mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Nuestra independencia no viene de buenas intenciones, sino de <strong>automatización
            total</strong>. No hay editores humanos seleccionando titulares o reordenando noticias.
          </p>

          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50/50 rounded-lg p-5 border border-green-100">
              <h4 className="font-serif font-bold text-green-800 mb-3 flex items-center gap-2">
                <span>🤖</span> 100% Automatizado
              </h4>
              <ul className="space-y-2 text-sm text-green-700">
                <li>• Pipeline ejecutado cada 30 minutos</li>
                <li>• Cero intervención humana en portada</li>
                <li>• Algoritmos matemáticos, no opiniones</li>
                <li>• Mismo proceso para todos los medios</li>
              </ul>
            </div>

            <div className="bg-blue-50/50 rounded-lg p-5 border border-blue-100">
              <h4 className="font-serif font-bold text-blue-800 mb-3 flex items-center gap-2">
                <span>📊</span> Métricas Cuantificables
              </h4>
              <ul className="space-y-2 text-sm text-blue-700">
                <li>• Similitud matemática (0-1)</li>
                <li>• Contraste de sesgo medible</li>
                <li>• Puntuación de relevancia (0-100)</li>
                <li>• Cobertura por espectro político (%)</li>
              </ul>
            </div>
          </div>

          <div className="bg-white/60 rounded-lg p-6 border border-stone-200 mb-6">
            <h4 className="font-mono text-sm font-semibold text-stone-500 uppercase tracking-wider mb-4">
              Medición matemática del sesgo
            </h4>
            <p className="text-stone-600 text-sm mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Para cada noticia, calculamos automáticamente:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center shrink-0">
                  <span className="text-red-600 text-xs font-bold">I</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-stone-900">Cobertura izquierda</p>
                  <p className="text-xs text-stone-500">% de medios de izquierda que cubren la noticia</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-stone-100 rounded flex items-center justify-center shrink-0">
                  <span className="text-stone-600 text-xs font-bold">C</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-stone-900">Cobertura centro</p>
                  <p className="text-xs text-stone-500">% de medios de centro que cubren la noticia</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center shrink-0">
                  <span className="text-blue-600 text-xs font-bold">D</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-stone-900">Cobertura derecha</p>
                  <p className="text-xs text-stone-500">% de medios de derecha que cubren la noticia</p>
                </div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-stone-50 rounded border border-stone-200">
              <p className="font-mono text-xs text-stone-600">
                contraste_sesgo = max(I, C, D) - min(I, C, D)
              </p>
              <p className="text-xs text-stone-500 mt-2">
                Si contraste {'>'} 0.3: hay narrativas divergentes. Si = 0: todos cubren igual.
              </p>
            </div>
          </div>

          <div className="bg-white/60 rounded-lg p-6 border border-stone-200 mb-6">
            <h4 className="font-mono text-sm font-semibold text-stone-500 uppercase tracking-wider mb-4">
              Monitorización continua
            </h4>
            <p className="text-stone-600 text-sm mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Rastreamos automáticamente:
            </p>
            <ul className="space-y-2 text-sm text-stone-700">
              <li className="flex items-start gap-2">
                <span className="text-blue-500">→</span>
                <span><strong>Errores del sistema:</strong> Sentry captura fallos técnicos en tiempo real</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500">→</span>
                <span><strong>Uso del pipeline:</strong> PostHog registra cada ejecución (scraping, clustering, análisis)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500">→</span>
                <span><strong>Calidad de clustering:</strong> Coherencia semántica promedio por cluster</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500">→</span>
                <span><strong>Cobertura semántica:</strong> % de artículos con análisis de significado</span>
              </li>
            </ul>
          </div>

          <InfoBox variant="success">
            <p className="text-sm text-green-800">
              <strong>✓ Garantía de independencia:</strong> Las métricas son matemáticas y el proceso
              no depende de decisiones humanas diarias. Si un medio no aparece, es porque no pasó los
              umbrales algorítmicos, no porque alguien lo censurara. Todo el proceso está documentado
              en esta página.
            </p>
          </InfoBox>
        </Section>

        {/* ============================================ */}
        {/* LIMITATIONS */}
        {/* ============================================ */}
        <Section title="Limitaciones Conocidas" icon="🚧">
          <p className="text-stone-700 mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            No somos perfectos. Aquí están nuestras limitaciones:
          </p>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-red-50/50 rounded-lg border border-red-100">
              <span className="text-red-500 text-xl shrink-0">✗</span>
              <div>
                <h4 className="font-semibold text-stone-900 mb-1">No verificamos hechos</h4>
                <p className="text-stone-600 text-sm">
                  Mostramos lo que dicen los medios, no si es verdad. Para fact-checking, 
                  recomendamos <a href="https://maldita.es" target="_blank" rel="noopener" className="text-blue-600 underline">Maldita.es</a>.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-red-50/50 rounded-lg border border-red-100">
              <span className="text-red-500 text-xl shrink-0">✗</span>
              <div>
                <h4 className="font-semibold text-stone-900 mb-1">Cobertura limitada</h4>
                <p className="text-stone-600 text-sm">
                  ~14 medios españoles. Faltan regionales, internacionales y especializados.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-red-50/50 rounded-lg border border-red-100">
              <span className="text-red-500 text-xl shrink-0">✗</span>
              <div>
                <h4 className="font-semibold text-stone-900 mb-1">Sesgo de selección</h4>
                <p className="text-stone-600 text-sm">
                  Al elegir qué medios incluir y qué temas priorizar, ya estamos introduciendo 
                  un sesgo. Intentamos ser representativos, pero toda selección es subjetiva.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-red-50/50 rounded-lg border border-red-100">
              <span className="text-red-500 text-xl shrink-0">✗</span>
              <div>
                <h4 className="font-semibold text-stone-900 mb-1">Automatización imperfecta</h4>
                <p className="text-stone-600 text-sm">
                  El clustering y la IA cometen errores. A veces agrupa artículos que no 
                  deberían estar juntos, o separa los que sí.
                </p>
              </div>
            </div>
          </div>
          
          <InfoBox variant="success">
            <p className="text-sm text-green-800">
              <strong>💬 Tu feedback importa:</strong> ¿Ves algo mal? 
              Escríbenos a <a href="mailto:hola@neotral.com" className="text-blue-600 underline">hola@neotral.com</a>.
            </p>
          </InfoBox>
        </Section>

        {/* ============================================ */}
        {/* WHAT WE ARE */}
        {/* ============================================ */}
        <Section title="Lo Que Somos (y No Somos)" icon="🎯">
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-green-50/50 rounded-lg p-6 border border-green-100">
              <h4 className="font-serif font-bold text-green-800 mb-4">✓ Somos</h4>
              <ul className="space-y-2 text-sm text-green-700">
                <li>• Un comparador de perspectivas</li>
                <li>• Una herramienta para ver todas las versiones</li>
                <li>• Honestos sobre nuestras decisiones</li>
                <li>• Un proyecto sin ánimo de lucro</li>
                <li>• Sin publicidad ni tracking</li>
              </ul>
            </div>
            
            <div className="bg-red-50/50 rounded-lg p-6 border border-red-100">
              <h4 className="font-serif font-bold text-red-800 mb-4">✗ No somos</h4>
              <ul className="space-y-2 text-sm text-red-700">
                <li>• La verdad absoluta</li>
                <li>• Un fact-checker</li>
                <li>• Completamente objetivos</li>
                <li>• Un sustituto de leer los medios</li>
                <li>• Perfectos (cometemos errores)</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-stone-900 text-white rounded-lg p-8 text-center">
            <h3 className="font-serif text-2xl font-bold mb-3">
              Nuestra filosofía
            </h3>
            <p className="text-stone-300 max-w-xl mx-auto" style={{ fontFamily: 'Georgia, serif' }}>
              "No te decimos qué pensar. Te mostramos todas las versiones para que 
              <em> tú decidas</em>. Preferimos ser honestos sobre nuestras limitaciones 
              a pretender que somos perfectos."
            </p>
            <p className="text-stone-500 text-sm mt-4">
              — Equipo Neotral
            </p>
          </div>
        </Section>

        {/* CTA final */}
        <div className="text-center py-8 border-t border-stone-300">
          <p className="text-stone-600 mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            ¿Tienes más preguntas? ¿Quieres colaborar?
          </p>
          <a 
            href="mailto:hola@neotral.com" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-stone-900 text-white rounded-lg font-medium hover:bg-stone-700 transition-colors"
          >
            <span>Escríbenos</span>
            <span>→</span>
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-300 mt-12 bg-[#FDF6E3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="w-2 h-2 rounded-full bg-stone-400"></span>
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              </div>
              <span className="font-serif font-bold text-stone-700">NEOTRAL</span>
            </div>
            
            <p className="text-xs text-stone-400 font-serif italic">
              Todas las versiones. Tú decides.
            </p>
            
            <p className="text-xs text-stone-400 font-mono">
              © {new Date().getFullYear()} Fundación MAFT
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
